import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../../config/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import "./CourseDetailPage.css";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import {
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaCog,
  FaPlus,
  FaClock,
  FaPen,
  FaLayerGroup,
  FaUsers,
} from "react-icons/fa";
import AvailabilitySettings from "./AvailabilitySettings";
import RubricEditor from "../gradebookpage/RubricEditor";
import SectionAvailabilityInfo from "./SectionAvailabilityInfo";
import ClassListPopUp from "../classlistspage/ClassListPopUp";
import { useTrainingButton } from "../../../hooks/useTrainingElement";

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [showSectionCreator, setShowSectionCreator] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [moduleCreatorSectionId, setModuleCreatorSectionId] = useState(null);
  const [newModuleName, setNewModuleName] = useState("");
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editedSectionName, setEditedSectionName] = useState("");
  const [editedModuleName, setEditedModuleName] = useState("");
  const [showClassListsMenu, setShowClassListsMenu] = useState(false);
  const [classLists, setClassLists] = useState([]);
  const [isClassListsLoading, setIsClassListsLoading] = useState(false);
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [hiddenButtonSections, setHiddenButtonSections] = useState(true);
  const [showAvailabilitySettings, setShowAvailabilitySettings] =
    useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [showRubricEditor, setShowRubricEditor] = useState(false);
  const [selectedRubric, setSelectedRubric] = useState([]);
  const [maxScore] = useState(100);
  const [isClassListModalOpen, setIsClassListModalOpen] = useState(false);
  const [currentClassList, setCurrentClassList] = useState(null);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Training system
  const { useTrainingTips } = require("../../../hooks/useTrainingTips");
  const { showTipForComponent, shouldShowTip, getTrainingIndicatorClass } =
    useTrainingTips("course_detail");
  const addSectionButton = useTrainingButton(
    "course_detail",
    "course_detail_add_section",
    "top"
  );

  // Helper function to create training props for mapped elements
  const createTrainingProps = (tipId, position = "top") => {
    return {
      className: getTrainingIndicatorClass(tipId, "section"),
      onMouseEnter: (event) => {
        if (shouldShowTip(tipId)) {
          showTipForComponent(tipId, event.currentTarget, position);
        }
      },
    };
  };

  const fetchClassLists = async () => {
    try {
      setIsClassListsLoading(true);
      const getClassListsFunction = httpsCallable(functions, "getClassLists");
      const result = await getClassListsFunction();
      if (result.data && result.data.classLists) {
        // Filter class lists to only include those associated with the current course
        const filteredClassLists = result.data.classLists.filter(
          (classList) =>
            Array.isArray(classList.courseIds) &&
            classList.courseIds.includes(courseId)
        );
        setClassLists(filteredClassLists);
      }
    } catch (error) {
      console.error("Error fetching class lists:", error);
    } finally {
      setIsClassListsLoading(false);
    }
  };

  const fetchClassListData = async () => {
    if (!isDataLoaded) {
      try {
        const getClassListDataFunction = httpsCallable(
          functions,
          "getClassListData"
        );
        const classListDataResult = await getClassListDataFunction();
        setTeachers(classListDataResult.data.teachers);
        setStudents(classListDataResult.data.students);

        const getCoursesFunction = httpsCallable(functions, "getCourses");
        const coursesResult = await getCoursesFunction();
        setCourses(coursesResult.data.courses || []);

        setIsDataLoaded(true);
      } catch (error) {
        console.error("Error fetching class list data:", error);
      }
    }
  };

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const courseDoc = await getDoc(doc(db, "courses", courseId));
        if (courseDoc.exists()) {
          const courseData = courseDoc.data();
          setCourse(courseData);
          const uniqueSections = Array.from(
            new Set((courseData.sections || []).map(JSON.stringify))
          ).map(JSON.parse);
          setSections(uniqueSections);
        } else {
          console.log("No such course!");
        }
      } catch (error) {
        console.error("Error fetching course:", error);
      }
    };

    fetchCourse();
    fetchClassLists(); // Fetch class lists when component mounts
  }, [courseId]);

  const handleClassSettingsClick = () => {
    setShowClassListsMenu(!showClassListsMenu);
  };

  const addSection = () => {
    setShowSectionCreator(true);
    setNewSectionName("");
  };

  const handleSaveSection = async () => {
    if (!newSectionName.trim()) {
      alert("Section name cannot be empty");
      return;
    }

    setIsSaving(true);
    try {
      // First create the section
      const createSectionFunction = httpsCallable(functions, "createSection");
      const sectionResult = await createSectionFunction({
        courseId,
        sectionName: newSectionName,
      });

      // Get the newly created section's ID from the response
      // Note: Your cloud function needs to return the section ID
      const newSectionId = sectionResult.data.sectionId;

      // Create the default module in the new section
      const createModuleFunction = httpsCallable(functions, "createModule");
      await createModuleFunction({
        courseId,
        sectionId: newSectionId,
        moduleName: "New Module 1",
      });

      // Fetch updated course data
      const courseDoc = await getDoc(doc(db, "courses", courseId));
      if (courseDoc.exists()) {
        const courseData = courseDoc.data();
        setSections(courseData.sections || []);
      }

      setShowSectionCreator(false);
      setNewSectionName("");
    } catch (error) {
      console.error("Error adding section: ", error);
      alert(`Error adding section: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const addModule = (sectionId) => {
    setModuleCreatorSectionId(sectionId);
    setNewModuleName("");
  };

  const handleSaveModule = async (sectionId) => {
    if (!newModuleName.trim()) {
      alert("Module name cannot be empty");
      return;
    }

    setIsSaving(true);
    try {
      const createModuleFunction = httpsCallable(functions, "createModule");
      await createModuleFunction({
        courseId,
        sectionId,
        moduleName: newModuleName,
      });

      const courseDoc = await getDoc(doc(db, "courses", courseId));
      if (courseDoc.exists()) {
        const courseData = courseDoc.data();
        setSections(courseData.sections || []);
      }

      setModuleCreatorSectionId(null);
      setNewModuleName("");
    } catch (error) {
      console.error("Error adding module: ", error);
      alert(`Error adding module: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (window.confirm("Are you sure you want to delete this section?")) {
      try {
        const deleteSectionFunction = httpsCallable(functions, "deleteSection");
        await deleteSectionFunction({ courseId, sectionId });

        const courseDoc = await getDoc(doc(db, "courses", courseId));
        if (courseDoc.exists()) {
          const courseData = courseDoc.data();
          setSections(courseData.sections || []);
        }
      } catch (error) {
        console.error("Error deleting section: ", error);
        alert(`Error deleting section: ${error.message}`);
      }
    }
  };

  const handleSectionNameChange = async (sectionId, newName) => {
    if (newName.trim() === "") return;

    setIsSaving(true);
    try {
      const updateSectionFunction = httpsCallable(functions, "updateSection");
      await updateSectionFunction({ courseId, sectionId, newName });

      setSections(
        sections.map((section) =>
          section.id === sectionId ? { ...section, name: newName } : section
        )
      );
      setEditingSectionId(null);
      setEditedSectionName("");
    } catch (error) {
      console.error("Error updating section name: ", error);
      alert(`Error updating section name: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleModuleNameChange = async (sectionId, moduleId, newName) => {
    if (newName.trim() === "") return;

    setIsSaving(true);
    try {
      const updateModuleFunction = httpsCallable(functions, "updateModule");
      await updateModuleFunction({ courseId, sectionId, moduleId, newName });

      setSections(
        sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                modules: section.modules.map((module) =>
                  module.id === moduleId ? { ...module, name: newName } : module
                ),
              }
            : section
        )
      );
      setEditingModuleId(null);
      setEditedModuleName("");
    } catch (error) {
      console.error("Error updating module name: ", error);
      alert(`Error updating module name: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteModule = async (sectionId, moduleId) => {
    if (window.confirm("Are you sure you want to delete this module?")) {
      try {
        const deleteModuleFunction = httpsCallable(functions, "deleteModule");
        await deleteModuleFunction({ courseId, sectionId, moduleId });

        const courseDoc = await getDoc(doc(db, "courses", courseId));
        if (courseDoc.exists()) {
          const courseData = courseDoc.data();
          setSections(courseData.sections || []);
        }
      } catch (error) {
        console.error("Error deleting module:", error);
        alert(`Failed to delete module. Error: ${error.message}`);
      }
    }
  };

  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const [movedSection] = newSections.splice(index, 1);
    newSections.splice(index + direction, 0, movedSection);
    setSections(newSections);
    saveOrderToDatabase(newSections);
  };

  const moveModule = (sectionIndex, moduleIndex, direction) => {
    const newSections = [...sections];
    const newModules = [...newSections[sectionIndex].modules];
    const [movedModule] = newModules.splice(moduleIndex, 1);
    newModules.splice(moduleIndex + direction, 0, movedModule);
    newSections[sectionIndex].modules = newModules;
    setSections(newSections);
    saveOrderToDatabase(newSections);
  };

  const saveOrderToDatabase = async (newSections) => {
    try {
      const updateCourseFunction = httpsCallable(functions, "updateCourse");
      await updateCourseFunction({ courseId, sections: newSections });
      console.log("Order saved successfully!");
    } catch (error) {
      console.error("Error saving order: ", error);
    }
  };

  const navigateToModule = (courseId, sectionId, moduleId) => {
    navigate(`/courses/${courseId}/sections/${sectionId}/modules/${moduleId}`);
  };

  const DeleteButton = ({ onClick, disabled, className, buttonType }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`control-button ${buttonType}-button ${className}`}
    >
      <FaTimes />
    </button>
  );

  const handleCourseClick = () => {
    navigate("/courses");
  };

  const handleModeToggle = async (sectionId, moduleId) => {
    try {
      const updateModuleModeFunction = httpsCallable(
        functions,
        "updateModuleMode"
      );
      await updateModuleModeFunction({
        courseId,
        sectionId,
        moduleId,
      });

      setSections((prevSections) =>
        prevSections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                modules: section.modules.map((module) =>
                  module.id === moduleId
                    ? {
                        ...module,
                        settings: {
                          ...module.settings,
                          isQuizMode: !module.settings?.isQuizMode,
                        },
                      }
                    : module
                ),
              }
            : section
        )
      );
    } catch (error) {
      console.error("Error updating module mode:", error);
      alert("Failed to update module mode. Please try again.");
    }
  };

  const handleVisibilityToggle = async (sectionId, isVisible) => {
    try {
      const updateSectionVisibilityFunction = httpsCallable(
        functions,
        "updateSectionVisibility"
      );
      await updateSectionVisibilityFunction({
        courseId,
        sectionId,
        isVisible: !isVisible,
      });

      setSections((prevSections) =>
        prevSections.map((section) =>
          section.id === sectionId
            ? { ...section, isVisible: !isVisible }
            : section
        )
      );
    } catch (error) {
      console.error("Error updating section visibility:", error);
      alert("Failed to update section visibility. Please try again.");
    }
  };

  const handleSectionSettings = () => {
    setHiddenButtonSections((prev) => !prev);
  };

  const handleAvailabilitySettings = async (sectionId, availabilityData) => {
    try {
      const updateSectionAvailabilityFunction = httpsCallable(
        functions,
        "updateSectionAvailability"
      );
      await updateSectionAvailabilityFunction({
        courseId,
        sectionId,
        availability: availabilityData,
      });

      setSections((prevSections) =>
        prevSections.map((section) =>
          section.id === sectionId
            ? { ...section, availability: availabilityData }
            : section
        )
      );
    } catch (error) {
      console.error("Error updating section availability:", error);
      alert("Failed to update section availability. Please try again.");
    }
  };

  const handleRubricSave = (rubric) => {
    setSelectedRubric(rubric);
    setShowRubricEditor(false);
  };

  const handleClassListClick = async (classList) => {
    setCurrentClassList(classList);
    if (!isDataLoaded) {
      await fetchClassListData();
    }
    setIsClassListModalOpen(true);
    setShowClassListsMenu(false);
  };

  const handleUpdateClassList = async (updatedClassList, classListId) => {
    try {
      const updateClassListFunction = httpsCallable(
        functions,
        "updateClassList"
      );
      await updateClassListFunction({
        classListId: classListId,
        updatedData: updatedClassList,
      });

      // Refresh the class lists after update
      await fetchClassLists();
      setIsClassListModalOpen(false);
    } catch (error) {
      console.error("Error updating class list:", error);
      alert(`Failed to update class list. Error: ${error.message}`);
    }
  };

  if (!course) {
    return <div className="loading-message">Loading...</div>;
  }

  return (
    <div className="course-detail-page fade-in">
      <div className="status-bar">
        <span
          className="status-field clickable-course-name"
          onClick={handleCourseClick}
        >
          Course: {course.courseName}
        </span>
        <span className="status-field">Owner: {course.ownerName}</span>
        <div className="status-field class-settings-container">
          <button
            className="class-settings-button"
            onClick={handleClassSettingsClick}
            title="Class Settings"
          >
            <FaUsers />
            <span className="class-button-text">Class</span>
          </button>
          {showClassListsMenu && (
            <div className="class-lists-menu">
              <h3>Class Lists</h3>
              {isClassListsLoading ? (
                <p>Loading class lists...</p>
              ) : classLists.length > 0 ? (
                <ul>
                  {classLists.map((classList) => (
                    <li
                      key={classList.id}
                      onClick={() => handleClassListClick(classList)}
                      className="clickable-class-list"
                    >
                      {classList.className}
                      <span className="class-list-info">
                        Students: {classList.students?.length || 0}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No class lists assigned to this course</p>
              )}
            </div>
          )}
        </div>
        <span className="status-field">
          Created:{" "}
          {course.creationDate
            ? new Date(course.creationDate.seconds * 1000).toLocaleString(
                "en-US",
                {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }
              )
            : "N/A"}
        </span>
        <span className="status-field">
          Edited:{" "}
          {course.lastEditDate
            ? new Date(course.lastEditDate.seconds * 1000).toLocaleString(
                "en-US",
                {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }
              )
            : "N/A"}
        </span>
      </div>
      {sections.length > 0 ? (
        sections.map((section, sectionIndex) => (
          <div
            key={section.id}
            className={`section-container ${
              createTrainingProps("course_detail_section", "right").className
            } ${!section.isVisible ? "hidden" : ""}`}
            onMouseEnter={
              createTrainingProps("course_detail_section", "right").onMouseEnter
            }
          >
            <div className="section-header">
              {editingSectionId === section.id ? (
                <div className="section-edit">
                  <input
                    type="text"
                    className="section-input"
                    value={editedSectionName}
                    onChange={(e) => setEditedSectionName(e.target.value)}
                    autoFocus
                  />
                  <div className="edit-buttons">
                    <button
                      className="save-button"
                      onClick={() =>
                        handleSectionNameChange(section.id, editedSectionName)
                      }
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </button>
                    <button
                      className="cancel-button"
                      onClick={() => {
                        setEditingSectionId(null);
                        setEditedSectionName("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="section-name-container">
                    <button
                      className="control-button visibility-button"
                      onClick={() =>
                        handleVisibilityToggle(section.id, section.isVisible)
                      }
                      title={
                        section.isVisible
                          ? "Hide from students"
                          : "Show to students"
                      }
                    >
                      {section.isVisible ? <FaEye /> : <FaEyeSlash />}
                    </button>
                    <span className="section-name">{section.name}</span>
                  </div>
                  <div className="section-header-buttons">
                    <button
                      className={`control-button add-module-button ${
                        hiddenButtonSections ? "hidden" : ""
                      }`}
                      onClick={() => addModule(section.id)}
                      title="Add a New Module"
                    >
                      <FaPlus />
                    </button>
                    <button
                      className={`control-button edit-name-button ${
                        hiddenButtonSections ? "hidden" : ""
                      }`}
                      onClick={() => {
                        setEditingSectionId(section.id);
                        setEditedSectionName(section.name);
                      }}
                      title="Edit Section Name"
                    >
                      <FaPen />
                    </button>
                    <button
                      className={`control-button availability-button ${
                        hiddenButtonSections ? "hidden" : ""
                      }`}
                      data-has-availability={
                        !!(
                          section.availability?.startDate ||
                          section.availability?.endDate
                        )
                      }
                      onClick={() => {
                        setSelectedSectionId(section.id);
                        setShowAvailabilitySettings(true);
                      }}
                    >
                      <div className="availability-tooltip">
                        <FaClock />
                        <div className="availability-tooltip-content">
                          <SectionAvailabilityInfo
                            availability={section.availability}
                          />
                        </div>
                      </div>
                    </button>
                    <button
                      className={`control-button ${
                        hiddenButtonSections ? "hidden" : ""
                      }`}
                      onClick={() => moveSection(sectionIndex, -1)}
                      disabled={sectionIndex === 0}
                      title="Move Section Up"
                    >
                      ▲
                    </button>
                    <button
                      className={`control-button ${
                        hiddenButtonSections ? "hidden" : ""
                      }`}
                      onClick={() => moveSection(sectionIndex, 1)}
                      disabled={sectionIndex === sections.length - 1}
                      title="Move Section Down"
                    >
                      ▼
                    </button>
                    <button
                      onClick={() => handleDeleteSection(section.id)}
                      className={`control-button ${
                        hiddenButtonSections ? "hidden" : ""
                      }`}
                      title="Delete Section"
                    >
                      <FaTimes />
                    </button>
                    <button
                      className="control-button"
                      onClick={() => handleSectionSettings()}
                      title="Section Settings"
                    >
                      <FaCog className="cog-icon" />
                    </button>
                  </div>
                </>
              )}
            </div>
            {section.modules &&
              section.modules.map((module, moduleIndex) => (
                <div
                  key={module.id}
                  className={`module-container ${
                    createTrainingProps("course_detail_module", "top").className
                  }`}
                  onMouseEnter={
                    createTrainingProps("course_detail_module", "top")
                      .onMouseEnter
                  }
                >
                  {editingModuleId === module.id ? (
                    <div className="module-edit">
                      <input
                        type="text"
                        className="module-input"
                        value={editedModuleName}
                        onChange={(e) => setEditedModuleName(e.target.value)}
                        autoFocus
                      />
                      <div className="edit-buttons">
                        <button
                          className="save-button"
                          onClick={() =>
                            handleModuleNameChange(
                              section.id,
                              module.id,
                              editedModuleName
                            )
                          }
                          disabled={isSaving}
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </button>
                        <button
                          className="cancel-button"
                          onClick={() => {
                            setEditingModuleId(null);
                            setEditedModuleName("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span
                        className="module-name"
                        onClick={() =>
                          navigateToModule(courseId, section.id, module.id)
                        }
                      >
                        {module.name}
                      </span>
                      <div
                        className={`mode-toggle ${
                          hiddenButtonSections ? "hidden" : ""
                        }`}
                      >
                        <label
                          className="mode-toggle-switch"
                          title="Switch Module to Practice or Quiz"
                        >
                          <input
                            type="checkbox"
                            checked={module.settings?.isQuizMode || false}
                            onChange={() =>
                              handleModeToggle(section.id, module.id)
                            }
                          />
                          <span className="mode-toggle-slider">
                            <span className="mode-toggle-letter">
                              {module.settings?.isQuizMode ? "Q" : "P"}
                            </span>
                          </span>
                        </label>
                      </div>
                      <button
                        className={`control-button edit-name-button ${
                          hiddenButtonSections ? "hidden" : ""
                        }`}
                        onClick={() => {
                          setEditingModuleId(module.id);
                          setEditedModuleName(module.name);
                        }}
                        title="Edit Module Name"
                      >
                        <FaPen />
                      </button>
                      <button
                        className={`control-button ${
                          hiddenButtonSections ? "hidden" : ""
                        }`}
                        onClick={() =>
                          moveModule(sectionIndex, moduleIndex, -1)
                        }
                        disabled={moduleIndex === 0}
                        title="Move Module Up"
                      >
                        ▲
                      </button>
                      <button
                        className={`control-button ${
                          hiddenButtonSections ? "hidden" : ""
                        }`}
                        onClick={() => moveModule(sectionIndex, moduleIndex, 1)}
                        disabled={moduleIndex === section.modules.length - 1}
                        title="Move Module Down"
                      >
                        ▼
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteModule(section.id, module.id)
                        }
                        className={`control-button ${
                          hiddenButtonSections ? "hidden" : ""
                        }`}
                        title="Delete Module"
                      >
                        <FaTimes />
                      </button>
                    </>
                  )}
                </div>
              ))}
            {moduleCreatorSectionId === section.id && (
              <div className="module-creator">
                <input
                  type="text"
                  className="module-input"
                  value={newModuleName}
                  onChange={(e) => setNewModuleName(e.target.value)}
                  placeholder="Enter a name for the module"
                />
                <div className="edit-buttons">
                  <button
                    className="save-button"
                    onClick={() => handleSaveModule(section.id)}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    className="cancel-button"
                    onClick={() => setModuleCreatorSectionId(null)}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      ) : (
        <p className="no-sections-message">
          No sections available. Create a new section to get started.
        </p>
      )}
      {showSectionCreator && (
        <div className="section-creator-container">
          <div className="section-creator">
            <input
              type="text"
              className="section-input"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              placeholder="Enter a name for your section"
            />
            <div className="edit-buttons">
              <button
                className="save-button"
                onClick={handleSaveSection}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                className="cancel-button"
                onClick={() => setShowSectionCreator(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="add-section-container">
        <button
          ref={addSectionButton.ref}
          className={`content-button ${addSectionButton.className}`}
          onClick={addSection}
          onMouseEnter={addSectionButton.onMouseEnter}
        >
          <FaLayerGroup className="button-icon" />
          Add Section
        </button>
      </div>
      {showAvailabilitySettings && (
        <AvailabilitySettings
          isOpen={showAvailabilitySettings}
          onClose={() => {
            setShowAvailabilitySettings(false);
            setSelectedSectionId(null);
          }}
          onSave={(availabilityData) => {
            handleAvailabilitySettings(selectedSectionId, availabilityData);
            setShowAvailabilitySettings(false);
            setSelectedSectionId(null);
          }}
          initialStartDate={
            sections.find((s) => s.id === selectedSectionId)?.availability
              ?.startDate
          }
          initialEndDate={
            sections.find((s) => s.id === selectedSectionId)?.availability
              ?.endDate
          }
          sectionId={selectedSectionId}
          courseId={courseId}
        />
      )}
      <RubricEditor
        show={showRubricEditor}
        onClose={() => setShowRubricEditor(false)}
        onSave={handleRubricSave}
        initialRubric={selectedRubric}
        maxScore={maxScore}
        courseId={courseId}
      />
      {isClassListModalOpen && (
        <ClassListPopUp
          classList={currentClassList}
          courses={courses}
          teachers={teachers}
          students={students}
          handleUpdateClassList={handleUpdateClassList}
          type="edit"
          handleClose={() => setIsClassListModalOpen(false)}
        />
      )}
    </div>
  );
};

export default CourseDetailPage;
